import React, { useEffect, useState } from "react";
import { ActivityIndicator, StyleSheet, View } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { MotiView } from "moti";

import AdminLayout from "../../layouts/AdminLayout.web";
import AdminTable from "../../components/adminDashboard/AdminTable/AdminTable";
import AdminTableHeader from "../../components/adminDashboard/AdminTable/AdminTableHeader";
import { StudentProfileTable } from "../../components/adminDashboard/StudentProfile";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomText from "../../components/common/general/CustomText/CustomText";
import { useAppConfig } from "../../AppConfigProvider";
import {
  FacultyQueryResponseData,
  GetUsersForSchoolQuery,
  useGetUsersForSchoolQuery,
} from "../../graphql/generated/graphql";
import type { RootStackParamList } from "../../navigation/AppNavigator.web";
import useSortingDropdown from "../../hooks/useSortingDropdown";
import usePagination from "../../hooks/usePagination";
import AdminTablePagination from "../../components/adminDashboard/AdminTable/AdminTablePagination";

interface TransformedStudent {
  id: string;
  name: string;
  email: string;
  status: string;
  dateEnrolled: string | null;
}

const SORT_ITEMS = [
  { label: "Date (Newest First)", value: "date_desc" },
  { label: "Date (Oldest First)", value: "date_asc" },
  { label: "Name (A-Z)", value: "name_asc" },
  { label: "Name (Z-A)", value: "name_desc" },
  { label: "Enrollment (High - Low)", value: "enrollment_desc" },
  { label: "Enrollment (Low - High)", value: "enrollment_asc" },
];

const deriveStudentStatus = (student: FacultyQueryResponseData): "INVITED" | "EXPIRED" | "PENDING" | "ENROLLED" => {
  // Platform-level invite
  if (student.pendingFacultyInvite) {
    return student.expiredInvitation ? "EXPIRED" : "INVITED";
  }

  // Course-level invite
  if (student.pendingCourseEnrollmentInvite) {
    return student.expiredInvitation ? "EXPIRED" : "PENDING";
  }

  return "ENROLLED";
};

const transformStudentData = (apiData: GetUsersForSchoolQuery | undefined): TransformedStudent[] => {
  if (!apiData?.getUsersForSchool?.data) return [];

  return apiData.getUsersForSchool.data.faculties.map(
    (student: FacultyQueryResponseData): TransformedStudent => ({
      id: student.id,
      name: student.name,
      email: student.email || "",
      status: deriveStudentStatus(student),
      dateEnrolled: student.created || null,
    })
  );
};

const StudentsListView: React.FC = () => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const appConfig = useAppConfig();
  const schoolId = appConfig.axioSchools[0];

  const [searchQuery, setSearchQuery] = useState("");

  const { sortBy, setSortBy, sortDropdownOpen, setSortDropdownOpen } = useSortingDropdown({
    defaultSortBy: "date_desc",
  });

  const { page, setPage, pageSize, setPageSize } = usePagination({ itemsPerPage: 50 });

  const {
    data: studentsData,
    loading,
    error,
    refetch: studentsRefetch,
  } = useGetUsersForSchoolQuery({
    variables: {
      schoolId,
      searchQuery,
      page,
      pageSize,
      sortBy,
      fetchStudentsOnly: true,
    },
    skip: !schoolId,
  });

  const students = transformStudentData(studentsData);
  const totalCount = studentsData?.getUsersForSchool?.data.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, sortBy]);

  useEffect(() => {
    studentsRefetch();
  }, [searchQuery, page, pageSize, sortBy]);

  const handleStudentSelect = (studentId: string) => {
    const selectedStudent = students.find((student) => student.id === studentId);
    const studentName = selectedStudent?.name || "Unknown Student";

    navigation.navigate("Student Profile", {
      studentId,
      studentName,
      returnTo: "Students",
    });
  };

  {
    /* Cannot invite students from a school level yet. The current mutation is for courses */
  }
  // const handleAddStudent = () => {
  //   // Navigate to add student form
  //   console.log("Add student functionality");
  // };

  return (
    <AdminLayout withPadding={false}>
      <View style={styles.pageBackground}>
        <View style={styles.contentContainer}>
          <MotiView
            from={{ opacity: 0, translateY: 20, scale: 1 }}
            animate={{ opacity: 1, translateY: 0, scale: 1 }}
            delay={10}
            // @ts-expect-error - transition is not typed
            transition={{ type: "timing", duration: 500 }}>
            <View style={styles.header}>
              <View style={styles.headerLeft}>
                <CustomText text="Students" size="m" textType="display" weight="ultraBold" style={styles.title} />
                <CustomText
                  text="Manage student enrollments, invitations, and academic records."
                  size="m"
                  style={styles.subtitle}
                />
              </View>
            </View>

            <CustomSpacing type="vertical" size="l" />

            <View style={styles.totalCountContainer}>
              <CustomText
                text={`Total Students (${totalCount})`}
                size="l"
                useTranslationText={false}
                weight="bold"
                style={styles.totalCountText}
              />

              {/* Cannot invite students from a school level yet. The current mutation is for courses */}
              {/* <CustomButton
                text="Invite Students"
                bold={true}
                leftIcon={<PlusCircle size={24} weight="bold" color="#FFFFFF" />}
                onPress={handleAddStudent}
                style={styles.addButton}
                textStyle={styles.addButtonText}
              /> */}
            </View>

            <CustomSpacing type="vertical" size="m" />

            <AdminTable>
              <AdminTableHeader
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortDropdownOpen={sortDropdownOpen}
                setSortDropdownOpen={setSortDropdownOpen}
                sortItems={SORT_ITEMS}
              />
              {loading ? (
                <View style={styles.loaderContainer}>
                  <ActivityIndicator size="large" color="#0084FF" />
                </View>
              ) : students.length > 0 ? (
                <StudentProfileTable students={students} onStudentSelect={handleStudentSelect} />
              ) : (
                <View style={styles.emptyState}>
                  <CustomText text="No students found." />
                </View>
              )}
              {totalCount > 0 && (
                <AdminTablePagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={totalCount}
                  itemsPerPage={pageSize}
                  onPageChange={setPage}
                  onItemsPerPageChange={(newPageSize) => {
                    setPageSize(newPageSize);
                    setPage(1);
                  }}
                />
              )}
            </AdminTable>
          </MotiView>
        </View>
      </View>
    </AdminLayout>
  );
};

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  pageBackground: {
    flex: 1,
    backgroundColor: "#F2F4F7",
    // @ts-expect-error - works on web
    minHeight: "100vh",
  },
  contentContainer: {
    padding: 24,
    flex: 1,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerLeft: {
    flex: 1,
  },
  title: {
    color: "#101828",
    marginBottom: 4,
  },
  subtitle: {
    color: "#6B7280",
  },
  totalCountContainer: {
    width: "100%",
    marginBottom: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  totalCountText: {
    color: "#111827",
  },
});

export default StudentsListView;
